import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { ORG_WIDE_REPORT_RESOURCE_ID } from '../features/reports/types';
import { fetchLegalRequirements } from '../features/legalRequirements/legalRequirementsApi';
import { AspectFormModal } from '../features/iso14001/aspects/AspectFormModal';
import { CriteriaConfigPanel } from '../features/iso14001/aspects/CriteriaConfigPanel';
import {
  createEnvironmentalAspect,
  escalateEnvironmentalAspect,
  fetchEnvironmentalAspects,
  fetchEnvironmentalCriteriaConfig,
  fetchLifecycleReport,
  updateEnvironmentalAspect,
  updateEnvironmentalCriteriaConfig,
} from '../features/iso14001/aspects/aspectsApi';
import type { LifecycleReportRow } from '../features/iso14001/aspects/aspectsApi';
import type { EnvironmentalAspectSummary, EnvironmentalCriteriaConfig } from '../features/iso14001/aspects/types';
import {
  CONDITION_LABEL,
  LIFECYCLE_STAGE_LABEL,
  LIFECYCLE_STAGE_OPTIONS,
  STATUS_LABEL,
  conditionBadgeClass,
  significanceBadgeClass,
  significanceLabel,
  statusBadgeClass,
} from '../features/iso14001/aspects/aspectsUtils';

export function EnvironmentalAspectsPage() {
  const { canEdit } = usePlatformAccessLevel('iso14001.aspects.access');
  const [items, setItems] = useState<EnvironmentalAspectSummary[]>([]);
  const [criteria, setCriteria] = useState<EnvironmentalCriteriaConfig | null>(null);
  const [legalRequirements, setLegalRequirements] = useState<
    { id: string; code: string; title: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterCondition, setFilterCondition] = useState('');
  const [filterSignificant, setFilterSignificant] = useState('');
  const [filterLifecycle, setFilterLifecycle] = useState('');
  const [lifecycleReport, setLifecycleReport] = useState<LifecycleReportRow[]>([]);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<EnvironmentalAspectSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, config, legal, report] = await Promise.all([
        fetchEnvironmentalAspects({
          page,
          pageSize: 20,
          condition: filterCondition || undefined,
          significant:
            filterSignificant === 'true'
              ? true
              : filterSignificant === 'false'
                ? false
                : undefined,
          lifecycleStage: filterLifecycle || undefined,
          search: search.trim() || undefined,
          sortBy: 'significanceScore',
          sortDir: 'desc',
        }),
        fetchEnvironmentalCriteriaConfig(),
        fetchLegalRequirements({ pageSize: 200, active: true }).catch(() => ({
          data: [],
          pagination: { pages: 1 },
        })),
        fetchLifecycleReport().catch(() => []),
      ]);
      setItems(list.data);
      setTotalPages(list.pagination.pages);
      setCriteria(config);
      setLifecycleReport(report);
      setLegalRequirements(
        legal.data.map((row) => ({ id: row.id, code: row.code, title: row.title })),
      );
    } catch {
      setError('No se pudieron cargar los aspectos ambientales.');
    } finally {
      setLoading(false);
    }
  }, [page, filterCondition, filterSignificant, filterLifecycle, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const threshold = criteria?.significanceThreshold ?? 12;
  const scaleMax = criteria?.scaleMax ?? 5;

  const escalate = async (item: EnvironmentalAspectSummary) => {
    await escalateEnvironmentalAspect(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Aspectos e impactos ambientales</h1>
          <p className="text-muted-foreground text-sm">ISO 14001 — matriz de significancia</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportPdfButton
            reportType="environmental_aspects"
            resourceId={ORG_WIDE_REPORT_RESOURCE_ID}
            permissionKey="iso14001.aspects.access"
            disabled={loading}
          />
          {canEdit && (
            <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
              Nuevo aspecto
            </button>
          )}
        </div>
      </div>

      <CriteriaConfigPanel
        config={criteria}
        canEdit={canEdit}
        onSave={async (payload) => {
          const result = await updateEnvironmentalCriteriaConfig(payload);
          setCriteria(result.config);
          await load();
        }}
      />

      {lifecycleReport.length > 0 && (
        <section className="rounded-lg border border-border p-4 space-y-3">
          <h2 className="text-sm font-semibold">Perspectiva de ciclo de vida (ISO 14001 §6.1.2)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="pb-2 pr-4">Etapa</th>
                  <th className="pb-2 pr-4">Total</th>
                  <th className="pb-2 pr-4">Significativos</th>
                  <th className="pb-2">Sin control</th>
                </tr>
              </thead>
              <tbody>
                {lifecycleReport.map((row) => (
                  <tr key={row.lifecycleStage} className="border-t border-border/60">
                    <td className="py-2 pr-4">
                      {LIFECYCLE_STAGE_LABEL[row.lifecycleStage] ?? row.lifecycleStage}
                    </td>
                    <td className="py-2 pr-4">{row.total}</td>
                    <td className="py-2 pr-4">{row.significantCount}</td>
                    <td className="py-2">{row.withoutControlCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Buscar actividad, aspecto o impacto…"
          className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterCondition}
          onChange={(e) => {
            setFilterCondition(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todas las condiciones</option>
          {Object.entries(CONDITION_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterSignificant}
          onChange={(e) => {
            setFilterSignificant(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Toda significancia</option>
          <option value="true">Solo significativos</option>
          <option value="false">No significativos</option>
        </select>
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterLifecycle}
          onChange={(e) => {
            setFilterLifecycle(e.target.value);
            setPage(1);
          }}
        >
          {LIFECYCLE_STAGE_OPTIONS.map((opt) => (
            <option key={opt.value || 'all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3">Actividad</th>
              <th className="p-3">Aspecto → Impacto</th>
              <th className="p-3">Condición</th>
              <th className="p-3">Ciclo de vida</th>
              <th className="p-3">Significancia</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Requisito legal</th>
              {canEdit && <th className="p-3">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="p-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="p-6 text-center text-muted-foreground">
                  Sin aspectos registrados.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-border align-top">
                  <td className="p-3">
                    <div className="font-medium">{item.activity}</div>
                    <div className="text-xs text-muted-foreground">{item.code}</div>
                  </td>
                  <td className="p-3">
                    <div>{item.aspect}</div>
                    <div className="text-muted-foreground text-xs">→ {item.impact}</div>
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${conditionBadgeClass(item.condition)}`}
                    >
                      {CONDITION_LABEL[item.condition]}
                    </span>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">
                    {item.lifecycleStage
                      ? LIFECYCLE_STAGE_LABEL[item.lifecycleStage] ?? item.lifecycleStage
                      : '—'}
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${significanceBadgeClass(item.significant, item.significanceScore, threshold)}`}
                    >
                      {significanceLabel(item.significant, item.significanceScore, threshold)}
                    </span>
                  </td>
                  <td className="p-3">
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(item.status)}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="p-3">
                    {item.legalRequirement ? (
                      <Link
                        to={`/requisitos-legales/${item.legalRequirement.id}`}
                        className="text-xs hover:underline"
                      >
                        {item.legalRequirement.code}
                      </Link>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="p-3 space-x-2 whitespace-nowrap">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        onClick={() => setEditItem(item)}
                      >
                        Editar
                      </button>
                      {item.bridgeRecordId ? (
                        <Link
                          to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                          className="text-xs text-primary hover:underline"
                        >
                          Ver NC
                        </Link>
                      ) : (
                        item.significant &&
                        !item.controlMeasures?.trim() && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-xs"
                            onClick={() => void escalate(item)}
                          >
                            Escalar CAPA
                          </button>
                        )
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <AspectFormModal
        open={createOpen}
        title="Nuevo aspecto ambiental"
        scaleMax={scaleMax}
        legalRequirements={legalRequirements}
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          await createEnvironmentalAspect(payload);
          await load();
        }}
      />

      <AspectFormModal
        open={Boolean(editItem)}
        title={`Editar ${editItem?.code ?? ''}`}
        scaleMax={scaleMax}
        legalRequirements={legalRequirements}
        initial={
          editItem
            ? {
                activity: editItem.activity,
                aspect: editItem.aspect,
                impact: editItem.impact,
                condition: editItem.condition,
                lifecycleStage: editItem.lifecycleStage,
                legalRequirementId: editItem.legalRequirement?.id ?? null,
                severity: editItem.severity,
                frequency: editItem.frequency,
                legalRelevance: editItem.legalRelevance,
                stakeholderConcern: editItem.stakeholderConcern,
                controlMeasures: editItem.controlMeasures,
              }
            : undefined
        }
        onClose={() => setEditItem(null)}
        onSubmit={async (payload) => {
          if (!editItem) return;
          await updateEnvironmentalAspect(editItem.id, payload);
          await load();
        }}
      />
    </div>
  );
}
